import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Flex, Grid, Group, InputLabel, NumberInput, Stack, Tabs, Text } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { IconPercentage } from '@tabler/icons-react';
import { t } from 'i18next';
import { Activities, useSimulation } from '../../../global/hooks/useSimulation.tsx';
import { useGetDefaultSimulation } from '../../../global/entities/Simulation/queries/useGetDefaultSimulation.tsx.tsx';
import { useGetSimulationById } from '../../../global/entities/Simulation/queries/useGetSimulationById.tsx';
import { sanitizeTime, TimeWrapper } from '../../../global/component/molecules/TimeWrapper/TimeWrapper.tsx';

interface ISimulationSettingModalProps {
    close: () => void;
}

const SimulationSettingModal: React.FC<ISimulationSettingModalProps> = inject()(
    observer(({ close }) => {
        const { data: defaultSimulation } = useGetDefaultSimulation();
        const { data: simulation } = useGetSimulationById();
        const {
            therapeuticParams,
            setTherapeuticParams,
            activities,
            setActivities,
            advancedSettings,
            setAdvancedSettings,
            setSimulationSettingModal,
            removeSeconds,
        } = useSimulation();
        const [period2Enabled, setPeriod2Enabled] = useState(!!activities?.period2Start);
        const [errors, setErrors] = useState<Record<string, boolean>>({});
        const [disableApply, setDisableApply] = useState(false);

        useEffect(() => {
            validateForm();
        }, [activities, therapeuticParams, advancedSettings]);

        const validateForm = () => {
            const timeRegex = /^(?:[01]?\d|2[0-3]):[0-5]\d:[0-5]\d$/;

            const newErrors: Record<string, boolean> = {
                lunchTime: !activities?.lunchTime || !timeRegex.test(activities.lunchTime),
                sleepTime: !activities?.sleepTime || !timeRegex.test(activities.sleepTime),
                period1Start: !activities?.period1Start || !timeRegex.test(activities.period1Start),
                period1End: !activities?.period1End || !timeRegex.test(activities.period1End),
                period2Start: period2Enabled
                    ? !activities?.period2Start || !timeRegex.test(activities.period2Start)
                    : false,
                period2End: period2Enabled ? !activities?.period2End || !timeRegex.test(activities.period2End) : false,
                therapeuticParams: Object.values(therapeuticParams ?? {}).some((val) => val === undefined || val < 0),
                advancedSettings: Object.values(advancedSettings ?? {}).some((val) => val === undefined || val < 0),
            };

            setErrors(newErrors);
            setDisableApply(Object.values(newErrors).some((err) => err === true));
        };

        const resetModal = () => {
            if (simulation) 
                setSimulationSettingModal(simulation);
             else if (defaultSimulation) 
                setSimulationSettingModal(defaultSimulation);
            
            close();
        };

        useEffect(() => {
            setActivities((prev: Activities | null) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    period2Start: period2Enabled ? activities?.period2Start || '18:00:00' : null,
                    period2End: period2Enabled ? activities?.period2End || '20:00:00' : null,
                };
            });
        }, [period2Enabled]);

        return (
            <>
                <Tabs defaultValue="therapeutic">
                    <Tabs.List>
                        <Tabs.Tab value="therapeutic">{t('formSimulation.therapeuticParameters')}</Tabs.Tab>
                        <Tabs.Tab value="activities">{t('formSimulation.activities')}</Tabs.Tab>
                        <Tabs.Tab value="advanced">{t('formSimulation.advancedSettings')}</Tabs.Tab>
                    </Tabs.List>

                    {/* Onglet THERAPEUTIC */}
                    <Tabs.Panel value="therapeutic" pt="md">
                        <Stack gap={'sm'}>
                            <Text py={'md'} c={'dimmed'} size={'md'}>
                                {t('formSimulation.therapeuticParametersText')}
                            </Text>
                            {[
                                { key: 'timePercentageInPeriod1', label: t('formSimulation.activeBox1Time') },
                                {
                                    key: 'responderPercentageInPeriod1',
                                    label: t('formSimulation.activeBox1Responders'),
                                },
                                { key: 'timePercentageInPeriod2', label: t('formSimulation.activeBox2Time') },
                                {
                                    key: 'responderPercentageInPeriod2',
                                    label: t('formSimulation.activeBox2Responders'),
                                },
                                { key: 'timePercentageInSleep', label: t('formSimulation.sleepBoxTime') },
                                { key: 'responderPercentageInSleep', label: t('formSimulation.sleepBoxResponders') },
                                {
                                    key: 'rollerCoasterEffectPercentage',
                                    label: t('formSimulation.rollerCoasterEffect'),
                                },
                            ].map(({ key, label }) => (
                                <Flex key={key} justify={'space-between'} align={'center'}>
                                    <InputLabel size="md">{label}</InputLabel>
                                    <NumberInput
                                        value={therapeuticParams?.[key]}
                                        error={errors.therapeuticParams}
                                        onChange={(e) =>
                                            setTherapeuticParams({
                                                ...therapeuticParams,
                                                [key]: Number(e),
                                            })
                                        }
                                        rightSection={<IconPercentage color={'#868E96'} />}
                                        placeholder={'---'}
                                        allowDecimal={false}
                                        styles={{ input: { width: '115px' } }}
                                        min={1}
                                        max={100}
                                    />
                                </Flex>
                            ))}
                        </Stack>
                    </Tabs.Panel>

                    {/* Onglet ACTIVITIES */}
                    <Tabs.Panel value="activities" pt="md">
                        <Stack gap={'md'}>
                            {[
                                { key: 'lunchTime', label: t('formSimulation.lunchTime') },
                                { key: 'sleepTime', label: t('formSimulation.sleepTime') },
                            ].map(({ key, label }) => (
                                <Flex key={key} justify={'flex-start'} align={'center'}>
                                    <InputLabel miw={100} size="md">
                                        {label}
                                    </InputLabel>
                                    <TimeWrapper
                                        value={sanitizeTime(removeSeconds(activities?.[key as keyof Activities]))}
                                        onChange={(e) => {
                                            setActivities((prev) => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    [key as keyof Activities]: `${sanitizeTime(e)}:00`,
                                                };
                                            });
                                        }}
                                        error={errors[key as keyof Activities]}
                                    />
                                </Flex>
                            ))}
                            <Text py={'md'} c={'dimmed'} size={'md'}>
                                {t('formSimulation.activitiesText')}
                            </Text>

                            {[
                                [
                                    { key: 'period1Start', label: t('formSimulation.firstWindow') },
                                    { key: 'period1End', label: t('formSimulation.till') },
                                ],
                                [
                                    { key: 'period2Start', label: t('formSimulation.secondWindow') },
                                    { key: 'period2End', label: t('formSimulation.till') },
                                ],
                            ].map((row, rowIndex) => (
                                <Flex align="center" gap="md" key={row[0].key} justify="space-between" w="100%">
                                    <Flex align="center" gap="md">
                                        {row.map(({ key, label }) => (
                                            <Flex key={key} gap="sm" align="center">
                                                <InputLabel size="md">{label}</InputLabel>
                                                <TimeWrapper
                                                    value={sanitizeTime(
                                                        removeSeconds(activities?.[key as keyof Activities]),
                                                    )}
                                                    onChange={(e) => {
                                                        setActivities((prev) => {
                                                            if (!prev) return prev;
                                                            return {
                                                                ...prev,
                                                                [key as keyof Activities]: `${sanitizeTime(e)}:00`,
                                                            };
                                                        });
                                                    }}
                                                    disabled={rowIndex === 1 && !period2Enabled}
                                                    error={errors[key as keyof Activities]}
                                                />
                                            </Flex>
                                        ))}
                                    </Flex>

                                    {rowIndex === 1 && (
                                        <Flex mr={'auto'} gap="xs">
                                            <Checkbox
                                                label={t('global.enabled')}
                                                checked={period2Enabled}
                                                onChange={(e) => setPeriod2Enabled(e.target.checked)}
                                            />
                                        </Flex>
                                    )}
                                </Flex>
                            ))}
                        </Stack>
                    </Tabs.Panel>

                    {/* Onglet ADVANCED */}
                    <Tabs.Panel value="advanced" pt="md">
                        <Text py={'md'} c={'dimmed'} size={'md'}>
                            {t('formSimulation.advancedSettingsText1')}
                        </Text>

                        <Grid>
                            <Grid.Col style={{ alignContent: 'space-around' }} span={6}>
                                <InputLabel miw={120} size="md">
                                    {t('formSimulation.therapeuticWindow')}
                                </InputLabel>
                            </Grid.Col>
                            <Grid.Col style={{ alignContent: 'space-around' }} span={3}>
                                <InputLabel miw={120} size="md">
                                    {t('formSimulation.cMin')}
                                </InputLabel>
                            </Grid.Col>
                            <Grid.Col style={{ alignContent: 'space-around' }} span={3}>
                                <InputLabel miw={120} size="md">
                                    {t('formSimulation.cMax')}
                                </InputLabel>
                            </Grid.Col>
                            <Grid.Col style={{ alignContent: 'space-around' }} span={6}>
                                <InputLabel miw={120} size="md">
                                    {t('formSimulation.halfDayAM')}
                                </InputLabel>
                            </Grid.Col>
                            {[
                                { key: 'morningCMin' },
                                { key: 'morningCMax' },
                                { label: t('formSimulation.halfDayPM') },
                                { key: 'afternoonCMin' },
                                { key: 'afternoonCMax' },
                                { label: t('formSimulation.evening') },
                                { key: 'eveningCMin' },
                                { key: 'eveningCMax' },
                            ].map(({ key, label }) => (
                                <Grid.Col style={{ alignContent: 'space-around' }} span={label ? 6 : 3} key={key}>
                                    <Flex justify={'flex-start'} align={'center'}>
                                        {label && (
                                            <InputLabel miw={120} size="md">
                                                {label}
                                            </InputLabel>
                                        )}
                                        {key && (
                                            <NumberInput
                                                value={advancedSettings?.[key]}
                                                error={errors.advancedSettings}
                                                onChange={(e) =>
                                                    setAdvancedSettings({
                                                        ...advancedSettings,
                                                        [key]: Number(e),
                                                    })
                                                }
                                                placeholder={'---'}
                                                styles={{ input: { width: '115px' } }}
                                                allowDecimal={false}
                                                min={0}
                                            />
                                        )}
                                    </Flex>
                                </Grid.Col>
                            ))}
                            <Text py={'md'} c={'dimmed'} size={'md'}>
                                {t('formSimulation.advancedSettingsText2')}
                            </Text>

                            <Flex justify={'flex-start'} align={'center'}>
                                <InputLabel miw={120} size="md">
                                    {t('formSimulation.threshold')}
                                </InputLabel>
                                <NumberInput
                                    value={advancedSettings?.threshold}
                                    error={errors.advancedSettings}
                                    onChange={(e) =>
                                        setAdvancedSettings({
                                            ...advancedSettings,
                                            threshold: Number(e),
                                        })
                                    }
                                    placeholder={'---'}
                                    styles={{ input: { width: '115px' } }}
                                    allowDecimal={false}
                                    min={0}
                                    max={100}
                                />
                            </Flex>
                        </Grid>
                    </Tabs.Panel>
                </Tabs>

                <Group mt={'xl'} justify={'flex-end'} w={'100%'}>
                    <Button onClick={resetModal}>{t('button.cancel')}</Button>
                    <Button disabled={disableApply} onClick={close}>
                        {t('button.apply')}
                    </Button>
                </Group>
            </>
        );
    }),
);

export default SimulationSettingModal;
